import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  faAnchor,
  faAnglesLeft,
  faAnglesRight,
  faEye,
  faFileLines,
  faShip,
} from '@fortawesome/free-solid-svg-icons';
import { Button, Empty, Form, Progress, Select, Space, Spin, Tag } from 'antd';
import { styled } from 'styled-components';
import { useVesselPositions } from '@/api/Vessel';
import { useTranslate } from '@/translations';
import type { TFunc } from '@/translations/hooks';
import {
  Map,
  VESSEL_MAP_ZOOM,
  VESSEL_MARKER_ICON,
  CustomTable,
  CustomColumns,
  Icon,
  TooltipIcon,
} from '@/ui/components';
import { VesselPositions } from '@/types';
import {
  formatPosition,
  portWithCountry,
  toVesselMapMarkers,
  uniqueValues,
  VESSEL_MAP_TABLET_MAX_WIDTH,
  VESSEL_MAP_WIDE_MIN_WIDTH,
  vesselMatchesSite,
  vesselSites,
  vesselTrackStatus,
} from './vesselMapView';
import { StyledLink } from '@/ui/globalStyles';
import { timeConverter } from '@/utils/helpers';
import { AddJourneyModal } from '../AddJourneyModal';
import { AddManifestModal } from '../ManifestModal';

type LiveVesselMapProps = {
  canOperate?: boolean;
  onOpenManifests: (vesselName: string) => void;
};

type AppliedFilters = {
  fromLocation?: string;
  toLocation?: string;
  vessel?: string;
  site?: string;
  status?: '' | 'enRoute' | 'atPort';
};

const labelled = (name?: string | null, code?: string | null) => {
  if (name && code) return `${name} (${code})`;
  return name || code || '';
};

const trackStatus = (vessel: VesselPositions) => {
  const operational = vessel.operationalStatus?.trim().toLowerCase();
  if (operational?.includes('port')) return 'atPort' as const;
  if (operational) return 'enRoute' as const;
  return vesselTrackStatus(vessel.status);
};

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: flex-end;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const Panel = styled.section`
  flex: 1;
  min-width: 0;
`;

const Split = styled.div<{ $stacked?: boolean }>`
  display: flex;
  align-items: stretch;
  gap: 16px;
  min-height: ${({ $stacked }) => ($stacked ? '0' : '640px')};
  flex-direction: ${({ $stacked }) => ($stacked ? 'column' : 'row')};
`;

const MapPane = styled.aside<{ $collapsed?: boolean; $stacked?: boolean }>`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  width: ${({ $collapsed, $stacked }) =>
    $stacked ? '100%' : $collapsed ? '44px' : 'clamp(360px, 38%, 560px)'};
  height: ${({ $collapsed, $stacked }) => {
    if ($stacked) return '420px';
    if ($collapsed) return 'auto';
    return '640px';
  }};
  align-self: ${({ $collapsed, $stacked }) =>
    $stacked || $collapsed ? 'stretch' : 'flex-start'};
  min-height: 0;
  overflow: hidden;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  transition: width 0.2s ease;
`;

const MapCanvas = styled.div`
  flex: 1;
  height: 0;
  min-height: 0;
`;

const MapHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 8px 8px 12px;
  border-bottom: 1px solid #e4e7ec;
  font-size: 13px;
  font-weight: 600;
`;

const MapToggle = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 4px 6px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #344054;
  cursor: pointer;
  line-height: 1;

  &:hover {
    background: #f2f4f7;
  }
`;

const MapCollapsedStrip = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;
  height: 100%;
  padding: 12px 0;
  border: none;
  background: transparent;
  color: #344054;
  cursor: pointer;

  &:hover {
    background: #f2f4f7;
  }
`;

const MapCollapsedLabel = styled.span`
  writing-mode: vertical-rl;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
`;

const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(
    () => window.matchMedia(query).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(query);
    const onChange = () => setMatches(media.matches);
    setMatches(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [query]);

  return matches;
};

const PanelTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 16px;
`;

const CARD_NAVY = '#041b53';

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const DetailCard = styled.section`
  padding: 16px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
`;

const CardHeading = styled.h4`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 14px;
  color: ${CARD_NAVY};
  font-size: 15px;
  font-weight: 700;
`;

const MetaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0px 8px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  line-height: 1.4;
`;

const MetaLabel = styled.span`
  flex: none;
  color: #667085;
`;

const MetaValue = styled.span`
  color: #1d2939;
  text-align: right;
`;

const RouteRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 16px;
  font-size: 14px;
`;

const RouteDot = styled.span`
  width: 14px;
  height: 14px;
  flex: none;
  border-radius: 50%;
  background: #f5b400;
`;

const RouteName = styled.span`
  color: ${CARD_NAVY};
  font-weight: 700;
`;

const RouteArrow = styled.span`
  color: #98a2b3;
`;

const RefSection = styled.div`
  border: 1px solid #eef2f6;
  border-radius: 8px;
  margin-bottom: 8px;
`;

const SectionLabel = styled.div`
  margin-bottom: 8px;
  padding: 8px;
  border-bottom: 1px solid #eef2f6;
  color: #344054;
  background: #f9fafb;
  font-size: 13px;
  font-weight: 600;
`;

const RefRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 8px;
  color: #1d2939;
  font-size: 13px;
`;

const RefCount = styled.span`
  color: #98a2b3;
  white-space: nowrap;
`;

const TotalLabel = styled.span`
  color: #1d2939;
  font-weight: 700;
`;

const TotalValue = styled.strong`
  color: #1d2939;
  font-size: 15px;
`;

const ProcessedValue = styled.strong`
  color: #16a34a;
`;

const RemainingValue = styled.strong`
  color: #f97316;
`;

const countWithPercent = (count?: number, percent?: number) => {
  if (count == null && percent == null) return '—';
  const countText = count == null ? '—' : count.toLocaleString();
  return percent == null ? countText : `${countText} (${percent}%)`;
};

const formatAisAge = (timestamp: string | undefined, t: TFunc) => {
  if (!timestamp) return '';
  const then = new Date(timestamp).getTime();
  if (!then) return '';
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 60) return t('vesselAisMinsAgo', { count: minutes });
  return t('vesselAisHoursAgo', { count: Math.round(minutes / 60) });
};

const VesselDetail = ({ vessel }: { vessel: VesselPositions }) => {
  const { t } = useTranslate();
  const destination = labelled(vessel.aisDestination, vessel.aisLocode);
  const originPort = portWithCountry(vessel.lastPort, vessel.lastCountry);
  const destinationPort = portWithCountry(
    vessel.aisDestination,
    vessel.aisLocode,
  );
  const position = formatPosition(vessel.lat, vessel.lon);
  const location = vessel.currentLocationName
    ? `${vessel.currentLocationName} (${position})`
    : position;
  const updatedAt = timeConverter(vessel.timestamp, 'DD/MM/YYYY HH:mm');
  const age = formatAisAge(vessel.timestamp, t);
  const progress =
    vessel.journeyProgressPercent == null
      ? undefined
      : Math.min(100, Math.max(0, vessel.journeyProgressPercent));
  const references = vessel.shippingReferences ?? [];
  const processedPercent =
    vessel.processedPercent ??
    (vessel.quantity && vessel.processedCount != null
      ? Math.round((vessel.processedCount / vessel.quantity) * 100)
      : undefined);
  const remainingCount =
    vessel.quantity != null && vessel.processedCount != null
      ? Math.max(vessel.quantity - vessel.processedCount, 0)
      : undefined;
  const remainingPercent =
    remainingCount != null && vessel.quantity
      ? Math.round((remainingCount / vessel.quantity) * 100)
      : undefined;
  const processedLabel = countWithPercent(
    vessel.processedCount,
    processedPercent,
  );
  const remainingLabel = countWithPercent(remainingCount, remainingPercent);
  const hasSnapshot =
    references.length > 0 ||
    vessel.quantity != null ||
    vessel.processedCount != null;

  return (
    <DetailGrid>
      <DetailCard>
        <CardHeading>
          <Icon icon={faShip} color={CARD_NAVY} />
          {t('vesselContext')}
        </CardHeading>
        <MetaList>
          <MetaRow>
            <MetaLabel>{t('currentLocation')}:</MetaLabel>
            <MetaValue>{location}</MetaValue>
          </MetaRow>
          <MetaRow>
            <MetaLabel>{t('currentDestination')}:</MetaLabel>
            <MetaValue>{destination || '—'}</MetaValue>
          </MetaRow>
          <MetaRow>
            <MetaLabel>{t('vesselSpeed')}:</MetaLabel>
            <MetaValue>
              {`${Number(vessel.speed).toFixed(1)} kn / ${vessel.heading}°`}
            </MetaValue>
          </MetaRow>
          <MetaRow>
            <MetaLabel>{t('lastAisUpdate')}:</MetaLabel>
            <MetaValue>
              {updatedAt ? `${updatedAt}${age ? ` (${age})` : ''}` : '—'}
            </MetaValue>
          </MetaRow>
          <MetaRow>
            <MetaLabel>{t('journeyProgress')}:</MetaLabel>
            {progress == null ? (
              <MetaValue>—</MetaValue>
            ) : (
              <Progress percent={progress} />
            )}
          </MetaRow>
        </MetaList>
        {originPort || destinationPort ? (
          <RouteRow>
            <RouteDot />
            <RouteName>{originPort || '—'}</RouteName>
            <RouteArrow>→</RouteArrow>
            <RouteDot />
            <RouteName>{destinationPort || '—'}</RouteName>
          </RouteRow>
        ) : null}
      </DetailCard>
      <DetailCard>
        <CardHeading>
          <Icon icon={faAnchor} color={CARD_NAVY} />
          {t('associatedGbaPortCalls')}
        </CardHeading>
        <Empty description="Coming soon" />
      </DetailCard>
      <DetailCard>
        <CardHeading>
          <Icon icon={faFileLines} color={CARD_NAVY} />
          {t('manifestSnapshot')}
        </CardHeading>
        {hasSnapshot ? (
          <>
            <RefSection>
              <SectionLabel>{t('shippingReferences')}</SectionLabel>
              {references.map((reference, index) => (
                <RefRow key={`${reference.reference}-${index}`}>
                  <span>{reference.reference}</span>
                  {reference.quantity == null ? null : (
                    <RefCount>
                      {t('vesselReferenceVehicles', {
                        count: reference.quantity.toLocaleString(),
                      })}
                    </RefCount>
                  )}
                </RefRow>
              ))}
            </RefSection>
            <MetaList>
              <MetaRow>
                <TotalLabel>{t('totalVehicles')}</TotalLabel>
                <TotalValue>
                  {vessel.quantity == null
                    ? '—'
                    : vessel.quantity.toLocaleString()}
                </TotalValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>{t('processed')}</MetaLabel>
                {processedLabel === '—' ? (
                  <MetaValue>—</MetaValue>
                ) : (
                  <ProcessedValue>{processedLabel}</ProcessedValue>
                )}
              </MetaRow>
              <MetaRow>
                <MetaLabel>{t('remaining')}</MetaLabel>
                {remainingLabel === '—' ? (
                  <MetaValue>—</MetaValue>
                ) : (
                  <RemainingValue>{remainingLabel}</RemainingValue>
                )}
              </MetaRow>
            </MetaList>
          </>
        ) : (
          <Empty description={t('noManifestSnapshot')} />
        )}
      </DetailCard>
    </DetailGrid>
  );
};

export const LiveVesselMap = ({
  canOperate,
  onOpenManifests,
}: LiveVesselMapProps) => {
  const { t } = useTranslate();
  const { data, isFetching } = useVesselPositions();
  const [filterForm] = Form.useForm<AppliedFilters>();
  const [applied, setApplied] = useState<AppliedFilters>({ status: '' });
  const [selectedMmsi, setSelectedMmsi] = useState<string>();
  const [addJourneyOpen, setAddJourneyOpen] = useState(false);
  const [addManifestOpen, setAddManifestOpen] = useState(false);
  const isStacked = useMediaQuery(
    `(max-width: ${VESSEL_MAP_TABLET_MAX_WIDTH}px)`,
  );
  const isWide = useMediaQuery(`(min-width: ${VESSEL_MAP_WIDE_MIN_WIDTH}px)`);
  const userToggledMap = useRef(false);
  const [mapCollapsed, setMapCollapsed] = useState(
    () =>
      !window.matchMedia(`(min-width: ${VESSEL_MAP_WIDE_MIN_WIDTH}px)`).matches,
  );

  useEffect(() => {
    if (isStacked || userToggledMap.current) return;
    setMapCollapsed(!isWide);
  }, [isStacked, isWide]);

  const showMap = isStacked || !mapCollapsed;
  const setMapOpen = (open: boolean) => {
    userToggledMap.current = true;
    setMapCollapsed(!open);
  };

  const vessels = useMemo(() => {
    const rows = data?.entities || [];
    return rows.filter((vessel) => {
      const destinationMatch =
        !applied.toLocation ||
        (vessel.aisDestination || '').toLowerCase() ===
          applied.toLocation.toLowerCase();
      const fromMatch =
        !applied.fromLocation ||
        (vessel.lastPort || '').toLowerCase() ===
          applied.fromLocation.toLowerCase();
      const vesselMatch =
        !applied.vessel ||
        vessel.shipName?.toLowerCase() === applied.vessel.toLowerCase();
      const siteMatch = vesselMatchesSite(vessel, applied.site);
      const statusMatch =
        !applied.status || trackStatus(vessel) === applied.status;
      return (
        destinationMatch && fromMatch && vesselMatch && siteMatch && statusMatch
      );
    });
  }, [applied, data?.entities]);

  const mapMarkers = useMemo(() => toVesselMapMarkers(vessels), [vessels]);
  const selected = vessels.find((vessel) => vessel.mmsi === selectedMmsi);
  const destinationOptions = uniqueValues(
    (data?.entities || []).map((vessel) => vessel.aisDestination),
  );
  const vesselOptions = uniqueValues(
    (data?.entities || []).map((vessel) => vessel.shipName),
  );
  const fromOptions = uniqueValues(
    (data?.entities || []).map((vessel) => vessel.lastPort),
  );
  const siteOptions = uniqueValues(
    (data?.entities || []).flatMap((vessel) => vesselSites(vessel)),
  );

  const columns: CustomColumns<VesselPositions> = [
    {
      dataIndex: 'shipName',
      title: t('vesselTitle'),
      width: 160,
      fixed: 'left',
    },
    {
      dataIndex: 'imo',
      title: 'IMO',
      width: 110,
      fixed: 'left',
      render: (_, vessel) => (
        <StyledLink onClick={() => setSelectedMmsi(vessel.mmsi)}>
          {vessel.imo || '—'}
        </StyledLink>
      ),
    },
    {
      dataIndex: 'lat',
      title: t('currentLocation'),
      width: 180,
      render: (_, vessel) =>
        vessel.currentLocationName
          ? `${vessel.currentLocationName} ${formatPosition(vessel.lat, vessel.lon)}`
          : formatPosition(vessel.lat, vessel.lon),
    },
    {
      title: t('currentDestination'),
      dataIndex: 'aisDestination',
      width: 180,
      render: (_, vessel) =>
        labelled(vessel.aisDestination, vessel.aisLocode) || '—',
    },
    {
      dataIndex: 'nextGbaCallName',
      title: t('nextGbaCall'),
      width: 160,
      render: (_, vessel) =>
        labelled(vessel.nextGbaCallName, vessel.nextGbaCallCode) || '—',
    },
    {
      dataIndex: 'gbaEta',
      title: t('gbaEta'),
      width: 180,
      render: (_, vessel) => timeConverter(vessel.gbaEta) || '—',
    },
    {
      dataIndex: 'shippingReferences',
      title: t('shippingRefs'),
      width: 140,
      render: (_, vessel) =>
        vessel.shippingReferences?.length ? (
          <Space direction="vertical" size={2}>
            {vessel.shippingReferences.map((reference, index) => (
              <Tag key={`${reference.reference}-${index}`}>
                {reference.reference}
              </Tag>
            ))}
          </Space>
        ) : (
          '—'
        ),
    },
    {
      dataIndex: 'quantity',
      title: t('quantity'),
      width: 90,
      render: (_, vessel) =>
        vessel.quantity == null ? '—' : vessel.quantity.toLocaleString(),
    },
    {
      dataIndex: 'processedCount',
      title: t('processed'),
      width: 150,
      render: (_, vessel) => {
        if (vessel.processedPercent == null || !vessel.quantity) {
          return '—';
        }
        return (
          <>
            <div className="d-flex align-items-center justify-content-between">
              <span>
                {vessel.processedCount || 0}/{vessel.quantity}
              </span>
              <span>{vessel.processedPercent}%</span>
            </div>
            <Progress percent={vessel.processedPercent} showInfo={false} />
          </>
        );
      },
    },
    {
      dataIndex: 'status',
      title: t('status'),
      width: 110,
      fixed: 'right',
      render: (_, vessel) => {
        const atPort = trackStatus(vessel) === 'atPort';
        return (
          <Tag color={atPort ? 'blue' : 'green'}>
            {vessel.operationalStatus || (atPort ? t('atPort') : t('enRoute'))}
          </Tag>
        );
      },
    },
    {
      dataIndex: 'mmsi',
      title: t('actions'),
      width: 90,
      align: 'center',
      fixed: 'right',
      render: (_, vessel) => (
        <Space>
          <TooltipIcon
            title={t('manifests')}
            icon={faShip}
            onClick={() => onOpenManifests(vessel.shipName)}
          />
          <TooltipIcon
            title={t('liveVesselMap')}
            icon={faEye}
            onClick={() => setSelectedMmsi(vessel.mmsi)}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <Toolbar>
        <Filters data-testid="vessel-filters">
          <Form
            form={filterForm}
            component="div"
            layout="vertical"
            style={{ display: 'contents' }}
            initialValues={{ status: '' }}
            onFinish={(values) => setApplied(values)}
          >
            <Form.Item
              name="fromLocation"
              label={t('fromLocation')}
              style={{ marginBottom: 0 }}
            >
              <Select
                allowClear
                showSearch
                style={{ width: 160 }}
                placeholder={t('chooseLocation')}
                loading={isFetching}
                notFoundContent={isFetching ? <Spin size="small" /> : undefined}
                options={fromOptions.map((value) => ({ value }))}
              />
            </Form.Item>
            <Form.Item
              name="toLocation"
              label={t('toLocation')}
              style={{ marginBottom: 0 }}
            >
              <Select
                allowClear
                showSearch
                style={{ width: 160 }}
                placeholder={t('chooseLocation')}
                loading={isFetching}
                notFoundContent={isFetching ? <Spin size="small" /> : undefined}
                options={destinationOptions.map((value) => ({ value }))}
              />
            </Form.Item>
            <Form.Item
              name="vessel"
              label={t('vesselTitle')}
              style={{ marginBottom: 0 }}
            >
              <Select
                allowClear
                showSearch
                style={{ width: 160 }}
                placeholder={t('chooseVessel')}
                loading={isFetching}
                notFoundContent={isFetching ? <Spin size="small" /> : undefined}
                options={vesselOptions.map((value) => ({ value }))}
              />
            </Form.Item>
            <Form.Item
              name="site"
              label={t('gbaSite')}
              style={{ marginBottom: 0 }}
            >
              <Select
                allowClear
                showSearch
                style={{ width: 160 }}
                placeholder={t('chooseSite')}
                loading={isFetching}
                notFoundContent={isFetching ? <Spin size="small" /> : undefined}
                options={siteOptions.map((value) => ({ value }))}
              />
            </Form.Item>
            <Form.Item
              name="status"
              label={t('status')}
              style={{ marginBottom: 0 }}
            >
              <Select
                style={{ width: 140 }}
                options={[
                  { value: '', label: t('allStatuses') },
                  { value: 'enRoute', label: t('enRoute') },
                  { value: 'atPort', label: t('atPort') },
                ]}
              />
            </Form.Item>
            <Button type="primary" htmlType="submit">
              {t('go')}
            </Button>
          </Form>
        </Filters>
        {canOperate ? (
          <Actions>
            <Button onClick={() => setAddJourneyOpen(true)}>
              {t('addJourneyPortCall')}
            </Button>
            <Button type="primary" onClick={() => setAddManifestOpen(true)}>
              {t('addManifest')}
            </Button>
          </Actions>
        ) : null}
      </Toolbar>
      <Split $stacked={isStacked}>
        <Panel data-testid="vessel-grid">
          <PanelTitle>
            {t('liveVesselsCount', { count: vessels.length })}
          </PanelTitle>
          <CustomTable
            loading={isFetching}
            rowKey="mmsi"
            dataSource={vessels}
            columns={columns}
            scroll={{ x: 1600 }}
            expandable={{
              expandedRowRender: (vessel) => <VesselDetail vessel={vessel} />,
            }}
          />
        </Panel>
        <MapPane
          $collapsed={!isStacked && mapCollapsed}
          $stacked={isStacked}
          data-testid="vessel-map-pane"
        >
          {showMap ? (
            <>
              {isStacked ? null : (
                <MapHeader>
                  <span>{t('liveVesselMap')}</span>
                  <MapToggle
                    type="button"
                    aria-label={`${t('collapse')} ${t('liveVesselMap')}`}
                    onClick={() => setMapOpen(false)}
                  >
                    <Icon icon={faAnglesRight} />
                  </MapToggle>
                </MapHeader>
              )}
              <MapCanvas>
                <Map
                  markers={mapMarkers}
                  selectedMarkerId={selected?.mmsi}
                  icon={VESSEL_MARKER_ICON}
                  zoom={VESSEL_MAP_ZOOM}
                  center={
                    selected
                      ? { lat: selected.lat, lng: selected.lon }
                      : undefined
                  }
                />
              </MapCanvas>
            </>
          ) : (
            <MapCollapsedStrip
              type="button"
              aria-label={`${t('expand')} ${t('liveVesselMap')}`}
              onClick={() => setMapOpen(true)}
            >
              <Icon icon={faAnglesLeft} />
              <MapCollapsedLabel>{t('liveVesselMap')}</MapCollapsedLabel>
            </MapCollapsedStrip>
          )}
        </MapPane>
      </Split>
      {addJourneyOpen ? (
        <AddJourneyModal open onClose={() => setAddJourneyOpen(false)} />
      ) : null}
      {addManifestOpen ? (
        <AddManifestModal open onClose={() => setAddManifestOpen(false)} />
      ) : null}
    </>
  );
};
